import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { GridStackRender, GridStackRenderProvider, useGridStackContext } from './GridStack';

// GridStack CSS imports - ORDER MATTERS!
import 'gridstack/dist/gridstack.css';
// Our custom CSS (theme-aware gridlines, widget borders, 13/15/18 column overrides)
import './GridStack/grid-stack-custom.css';
import { Box } from '@mui/material';
import { GridStackWidget } from 'gridstack';
import { useDroppable } from '@dnd-kit/core';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import { database } from '../../utils/sensor-data/database.ts';
import { getActiveRunTail } from '../../utils/sensor-data/database.ts';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  appendCustomCallableStreamSampleToGraphs,
  appendElectronicsPortStreamSampleToGraphs,
  buildCustomCallableStreamGraph,
  buildElectronicsPortStreamGraph,
  buildLiveGraphSnapshot,
  EMPTY_CHART_PLACEHOLDER,
  EMPTY_SENSOR_OBJECTS,
  appendPythonStreamToScopedDrivebaseGraphs,
  drivebaseGraphKeysForStreamKeys,
  getSensorDataObjects,
  MOTOR_WIDGET_TYPE,
  OTHER_WIDGET_TYPE,
  SENSOR_OBJECTS,
  SENSOR_WIDGET_TYPE,
  snapshotElectronicsPortStreamGraphs,
} from '../Graphs/sensor-utils.ts';
import { useLiveGraphPublish } from '../../hooks/dashboard/useLiveGraphPublish';
import {
  collectFloatStreamGraphKeysFromLayout,
  collectElectronicSensorStreamKeysFromLayout,
  collectMotorStreamKeysFromLayout,
  collectOtherElectronicsStreamKeysFromLayout,
  collectUnionSensorKeysFromLayout,
} from '../../utils/sensor-stream-tracker';
import { forEachObjIndexed, isEmpty, last } from 'ramda';
import { applicationSlice } from '../../redux/ApplicationSlice.js';
import cloneDeep from 'lodash/cloneDeep';
import { Graph, PythonStreamData, SensorData, SensorRun } from './dashboardTypes.js';
import { ApplicationActions, RobotActions } from '../../utils/constants.ts';

import {
  detectProgramStopTransition,
  resolveHistoricLayout,
  resolveHistoricRun,
  shouldAutoSelectLatestRunDate,
  shouldClearHistoricGraphsToPlaceholder,
  shouldDeferHistoricLoad,
} from './dashboardHistoricUtils';
import { stableJsonSignature } from '../../utils/stableJsonSignature';
import { useDashboardCapabilities } from './dashboardCapabilities';
import { useDashboardProgramRunning, type LiveGraphStoreInstance } from './LiveGraphStore';

const { LIVE, HISTORIC } = ApplicationActions.DashboardViewMode;

interface DashboardProps {
  drawerOpen: boolean;
  liveGraphStore: LiveGraphStoreInstance;
  canvasWidth?: number; // Optional canvas width for fixed canvas sizing
  showGridLines?: boolean; // When true, grid lines are visible (e.g. when dragging from drawer)
}

interface DashboardHandle {
  addWidget: (fn: (id: string) => Omit<GridStackWidget, 'id'>) => void;
  getGridStack?: () => unknown;
}

// ! Content must be json string like this:
// { name: "Text", props: { content: "Item 1" } }
const Dashboard = forwardRef<DashboardHandle, DashboardProps>(function DashboardInner(
  { canvasWidth, showGridLines = false, liveGraphStore }: DashboardProps,
  ref: React.ForwardedRef<DashboardHandle>
) {
  const sensorObjectsRef = useRef<{ [key: string]: Graph }>({});
  const electronicsPortStreamGraphsRef = useRef<Record<string, Graph>>({});
  const customCallableStreamGraphsRef = useRef<Record<string, Graph>>({});
  const { gridStack, addWidget, loadWidgets } = useGridStackContext();
  const loadedProject = useAppSelector((state) => state.project.loadedProject);
  const identifier = useAppSelector((state) => state.project.loadedProject?.id);
  const allData = useLiveQuery(() => {
    if (!identifier) return [];
    return database.runData.where('programId').equals(identifier).toArray();
  }) as Array<SensorRun>;

  const { SEND_STARTING, STARTING, STOP, STOPPED } = RobotActions.PROGRAM;
  const programState = useAppSelector((state) => state.project.programState);
  const liveSensorData = useAppSelector((state) => state.robot.liveSensorData);
  const isProgramRunning = useDashboardProgramRunning();
  const { canInteractWithGrid } = useDashboardCapabilities();
  const runDate = useAppSelector((state) => state.application.runDate);
  const savedLiveLayout = useAppSelector((state) => state.application.saveLayout);
  const dashboardViewMode = useAppSelector((state) => state.application.dashboardViewMode);
  const isLiveMode = dashboardViewMode === LIVE;
  const liveLayout = useMemo(() => loadedProject?.dashboardLayout ?? [], [loadedProject?.dashboardLayout]);
  const historicLayout = useMemo(() => resolveHistoricLayout(allData ?? [], runDate), [allData, runDate]);
  const activeLayoutForStreams = useMemo(() => {
    return isLiveMode ? liveLayout : historicLayout;
  }, [historicLayout, isLiveMode, liveLayout]);
  const motorStreamKeys = useMemo(() => collectMotorStreamKeysFromLayout(activeLayoutForStreams), [activeLayoutForStreams]);
  const electronicSensorStreamKeys = useMemo(() => collectElectronicSensorStreamKeysFromLayout(activeLayoutForStreams), [activeLayoutForStreams]);
  const otherElectronicsStreamKeys = useMemo(() => collectOtherElectronicsStreamKeysFromLayout(activeLayoutForStreams), [activeLayoutForStreams]);
  const floatStreamGraphKeys = useMemo(() => collectFloatStreamGraphKeysFromLayout(activeLayoutForStreams), [activeLayoutForStreams]);
  const layoutStreamKeys = useMemo(() => collectUnionSensorKeysFromLayout(activeLayoutForStreams), [activeLayoutForStreams]);
  const activeDrivebaseGraphKeys = useMemo(() => drivebaseGraphKeysForStreamKeys(layoutStreamKeys), [layoutStreamKeys]);
  const { schedulePublish, flushPublish, cancelScheduledPublish } = useLiveGraphPublish();

  const dispatch = useAppDispatch();
  const prevIsProgramRunningRef = useRef(isProgramRunning);
  const skipNextHistoricalLoadRef = useRef(false);
  const prevAllDataLengthRef = useRef(0);
  const lastHydratedStoppedRunIdRef = useRef<number | null>(null);
  const prevDashboardViewModeRef = useRef(dashboardViewMode);
  const prevDashboardViewModeForLayoutRef = useRef(dashboardViewMode);
  const lastNonEmptyHistoricLayoutRef = useRef<GridStackWidget[] | null>(null);
  const lastLoadedLayoutSignatureRef = useRef<string | null>(null);

  // Prevent cross-project flicker: do not reuse a previous project's historic layout
  // while Dexie is still loading runs for the newly selected project.
  useEffect(() => {
    lastNonEmptyHistoricLayoutRef.current = null;
    lastLoadedLayoutSignatureRef.current = null;
  }, [identifier]);

  useImperativeHandle(ref, () => ({
    addWidget,
    getColumnCount: () => gridStack?.getColumn() ?? 0,
    getCellHeight: () => gridStack?.getCellHeight() ?? 0,
    getGridStack: () => gridStack,
  }));

  useEffect(() => {
    if (!gridStack) return;

    const justReturnedToLive = prevDashboardViewModeForLayoutRef.current === HISTORIC && dashboardViewMode === LIVE;
    // When switching historic sessions, `historicLayout` can briefly be empty while the new run resolves.
    // We only want to skip loading in that *transient* state. If the target run exists and its saved
    // dashboard layout is intentionally empty, we *must* load `[]` to clear stale widgets.
    const hasTargetHistoricRun = !isLiveMode && runDate != null && (allData ?? []).some((r) => r.runId === runDate);
    const shouldSkipHistoricLoad = !isLiveMode && historicLayout.length === 0 && !hasTargetHistoricRun;

    // If the target historic run exists and saved an intentionally empty layout, we must clear the grid.
    // Do not fall back to the previous non-empty historic layout in that case.
    const effectiveHistoricLayout = historicLayout.length > 0 ? historicLayout : hasTargetHistoricRun ? [] : lastNonEmptyHistoricLayoutRef.current;
    const layoutToLoad = isLiveMode ? (justReturnedToLive ? null : liveLayout) : effectiveHistoricLayout ?? [];

    // `loadWidgets([])` intentionally clears stale widgets while preserving the empty live grid for new drops.
    if (!shouldSkipHistoricLoad && layoutToLoad) {
      const layoutSignature = stableJsonSignature(layoutToLoad);
      if (lastLoadedLayoutSignatureRef.current !== layoutSignature) {
        lastLoadedLayoutSignatureRef.current = layoutSignature;
        loadWidgets(layoutToLoad);
      }
    }

    // GridStack stays enabled during program runs so users can reposition and resize widgets on the canvas.
    if (!canInteractWithGrid) {
      gridStack.disable();
    } else {
      gridStack.enable();
    }
  }, [allData, canInteractWithGrid, dashboardViewMode, gridStack, historicLayout, isLiveMode, liveLayout, loadWidgets, runDate]);

  // Remember the most recent non-empty historic layout so switching sessions doesn't flash an empty/default grid.
  useEffect(() => {
    if (dashboardViewMode !== HISTORIC) return;
    if (historicLayout.length === 0) return;
    lastNonEmptyHistoricLayoutRef.current = historicLayout;
  }, [dashboardViewMode, historicLayout]);

  useEffect(() => {
    const previousMode = prevDashboardViewModeRef.current;
    if (previousMode === LIVE && dashboardViewMode === HISTORIC) {
      dispatch(applicationSlice.actions.onSaveLayout(liveLayout));
    }
    prevDashboardViewModeRef.current = dashboardViewMode;
  }, [dashboardViewMode, dispatch, liveLayout]);

  useEffect(() => {
    const previousMode = prevDashboardViewModeForLayoutRef.current;
    if (previousMode === HISTORIC && dashboardViewMode === LIVE) {
      loadWidgets(savedLiveLayout);
      dispatch(applicationSlice.actions.onSaveLayout([]));
    }
    prevDashboardViewModeForLayoutRef.current = dashboardViewMode;
  }, [dashboardViewMode, savedLiveLayout, loadWidgets, gridStack, dispatch]);

  const { setNodeRef } = useDroppable({
    id: 'grid-stack',
  });

  const getGraphs = useCallback(
    (_sensorData: Array<SensorData>) => {
      const clearData = (value: Graph) => {
        if (value.data.length) {
          value.data = [];
        }
      };
      const _sensorObjects = cloneDeep(!allData || allData.length === 0 ? EMPTY_SENSOR_OBJECTS : SENSOR_OBJECTS);
      forEachObjIndexed(clearData, _sensorObjects);
      const historicDrivebaseKeys = drivebaseGraphKeysForStreamKeys(layoutStreamKeys);
      const appendPythonStreamPerGraph = (value: PythonStreamData) => {
        appendPythonStreamToScopedDrivebaseGraphs(_sensorObjects, value, historicDrivebaseKeys);
      };
      const electronicsPortStreamGraphs: Record<string, Graph> = {};
      for (const sensorKey of motorStreamKeys) {
        electronicsPortStreamGraphs[sensorKey] = cloneDeep(buildElectronicsPortStreamGraph(sensorKey, MOTOR_WIDGET_TYPE));
      }
      for (const sensorKey of electronicSensorStreamKeys) {
        electronicsPortStreamGraphs[sensorKey] = cloneDeep(buildElectronicsPortStreamGraph(sensorKey, SENSOR_WIDGET_TYPE));
      }
      for (const sensorKey of otherElectronicsStreamKeys) {
        electronicsPortStreamGraphs[sensorKey] = cloneDeep(buildElectronicsPortStreamGraph(sensorKey, OTHER_WIDGET_TYPE));
      }
      const customCallableStreamGraphs: Record<string, Graph> = {};
      for (const streamKey of floatStreamGraphKeys) {
        customCallableStreamGraphs[streamKey] = cloneDeep(buildCustomCallableStreamGraph(streamKey));
      }
      _sensorData.forEach((datum) => {
        if (!datum.pythonStream) return;
        appendPythonStreamPerGraph(datum.pythonStream);
        appendElectronicsPortStreamSampleToGraphs(electronicsPortStreamGraphs, datum.pythonStream, motorStreamKeys, MOTOR_WIDGET_TYPE);
        appendElectronicsPortStreamSampleToGraphs(electronicsPortStreamGraphs, datum.pythonStream, electronicSensorStreamKeys, SENSOR_WIDGET_TYPE);
        appendElectronicsPortStreamSampleToGraphs(electronicsPortStreamGraphs, datum.pythonStream, otherElectronicsStreamKeys, OTHER_WIDGET_TYPE);
        appendCustomCallableStreamSampleToGraphs(customCallableStreamGraphs, datum.pythonStream, floatStreamGraphKeys);
      });
      const values: Array<Graph> = [];
      Object.values(_sensorObjects).forEach((value) => {
        const _sensorObject = value as Graph;
        if (_sensorObject.data.length > 0) {
          // Historical/session data can start at the first received packet (e.g., 1.2s).
          // Normalize each time-series to start at t=0 for consistent session playback.
          const firstTimed = _sensorObject.data.find((datum) => typeof (datum as { time?: unknown }).time === 'number') as
            | { time?: number }
            | undefined;
          if (firstTimed && typeof firstTimed.time === 'number' && firstTimed.time !== 0) {
            const offset = firstTimed.time;
            _sensorObject.data = _sensorObject.data.map((datum) => {
              const maybeTimed = datum as { time?: number };
              if (typeof maybeTimed.time !== 'number') return datum;
              return { ...datum, time: Math.max(0, maybeTimed.time - offset) };
            });
          }
          values.push(_sensorObject);
        }
      });
      snapshotElectronicsPortStreamGraphs(electronicsPortStreamGraphs).forEach((streamGraph) => {
        if (streamGraph.data.length > 0) values.push(streamGraph);
      });
      snapshotElectronicsPortStreamGraphs(customCallableStreamGraphs).forEach((streamGraph) => {
        if (streamGraph.data.length > 0) values.push(streamGraph);
      });
      return values;
    },
    [allData, electronicSensorStreamKeys, floatStreamGraphKeys, layoutStreamKeys, motorStreamKeys, otherElectronicsStreamKeys]
  );

  const publishLiveGraphSnapshot = useCallback(() => {
    const snapshot = buildLiveGraphSnapshot({
      sensorObjects: sensorObjectsRef.current,
      activeDrivebaseGraphKeys,
      electronicsPortStreamGraphs: electronicsPortStreamGraphsRef.current,
      customCallableStreamGraphs: customCallableStreamGraphsRef.current,
    });
    liveGraphStore.publish(snapshot);
  }, [activeDrivebaseGraphKeys, liveGraphStore]);

  const appendLiveSample = useCallback(
    (sample: PythonStreamData) => {
      appendPythonStreamToScopedDrivebaseGraphs(sensorObjectsRef.current, sample, activeDrivebaseGraphKeys);
      appendElectronicsPortStreamSampleToGraphs(electronicsPortStreamGraphsRef.current, sample, motorStreamKeys, MOTOR_WIDGET_TYPE);
      appendElectronicsPortStreamSampleToGraphs(electronicsPortStreamGraphsRef.current, sample, electronicSensorStreamKeys, SENSOR_WIDGET_TYPE);
      appendElectronicsPortStreamSampleToGraphs(electronicsPortStreamGraphsRef.current, sample, otherElectronicsStreamKeys, OTHER_WIDGET_TYPE);
      appendCustomCallableStreamSampleToGraphs(customCallableStreamGraphsRef.current, sample, floatStreamGraphKeys);
    },
    [activeDrivebaseGraphKeys, electronicSensorStreamKeys, floatStreamGraphKeys, motorStreamKeys, otherElectronicsStreamKeys]
  );

  useEffect(() => {
    if (!isLiveMode || !isProgramRunning || !liveSensorData) return;

    let sensorObjects = sensorObjectsRef.current;
    if (isEmpty(sensorObjects)) {
      sensorObjects = cloneDeep(getSensorDataObjects());
      const clearData = (value: Graph) => {
        if (value.data.length) value.data = [];
      };
      forEachObjIndexed(clearData, sensorObjects);
      // Re-seed from the full in-memory active run so charts don't restart from empty
      // when navigating away/back during an active program run.
      const recent = getActiveRunTail() ?? [];
      if (recent.length) {
        recent.forEach((datum) => {
          if (!datum.pythonStream) return;
          appendPythonStreamToScopedDrivebaseGraphs(sensorObjects, datum.pythonStream, activeDrivebaseGraphKeys);
          appendElectronicsPortStreamSampleToGraphs(electronicsPortStreamGraphsRef.current, datum.pythonStream, motorStreamKeys, MOTOR_WIDGET_TYPE);
          appendElectronicsPortStreamSampleToGraphs(
            electronicsPortStreamGraphsRef.current,
            datum.pythonStream,
            electronicSensorStreamKeys,
            SENSOR_WIDGET_TYPE
          );
          appendElectronicsPortStreamSampleToGraphs(
            electronicsPortStreamGraphsRef.current,
            datum.pythonStream,
            otherElectronicsStreamKeys,
            OTHER_WIDGET_TYPE
          );
          appendCustomCallableStreamSampleToGraphs(customCallableStreamGraphsRef.current, datum.pythonStream, floatStreamGraphKeys);
        });
      }
      sensorObjectsRef.current = sensorObjects;
    }

    const stream = liveSensorData.pythonStream;
    if (stream) {
      appendLiveSample(stream);
    }

    schedulePublish(publishLiveGraphSnapshot);
  }, [
    activeDrivebaseGraphKeys,
    appendLiveSample,
    electronicSensorStreamKeys,
    floatStreamGraphKeys,
    liveSensorData,
    isLiveMode,
    isProgramRunning,
    motorStreamKeys,
    otherElectronicsStreamKeys,
    publishLiveGraphSnapshot,
    schedulePublish,
  ]);

  useEffect(() => {
    if (programState !== STOP && programState !== STOPPED) return;
    flushPublish();
    if (programState === STOP) {
      sensorObjectsRef.current = {};
      electronicsPortStreamGraphsRef.current = {};
      customCallableStreamGraphsRef.current = {};
      liveGraphStore.clear();
    }
  }, [flushPublish, liveGraphStore, programState, STOP, STOPPED]);

  useEffect(() => {
    if (!isLiveMode || isProgramRunning) return;
    cancelScheduledPublish();
  }, [cancelScheduledPublish, isLiveMode, isProgramRunning]);

  // Reset stopped-run hydration marker when a new run starts.
  useEffect(() => {
    if (programState === STARTING || programState === SEND_STARTING) {
      lastHydratedStoppedRunIdRef.current = null;
    }
  }, [programState, STARTING, SEND_STARTING]);

  // Clear chart buffers only at explicit start transitions so route changes while running
  // do not wipe the visible time window and restart charts from empty.
  // Use 2-point placeholder (time=0, time=1) so chart displays axes with a flat line.
  useEffect(() => {
    if (!isLiveMode) return;
    const shouldClear = programState === STARTING || programState === SEND_STARTING;
    if (shouldClear) {
      sensorObjectsRef.current = {};
      electronicsPortStreamGraphsRef.current = {};
      customCallableStreamGraphsRef.current = {};
      liveGraphStore.clear();
      dispatch(applicationSlice.actions.onSaveGraphs(getGraphs(EMPTY_CHART_PLACEHOLDER as unknown as SensorData[])));
    }
  }, [programState, STARTING, SEND_STARTING, dispatch, getGraphs, isLiveMode, liveGraphStore]);

  // Reset run-count ref when switching projects so we correctly detect new runs
  useEffect(() => {
    prevAllDataLengthRef.current = 0;
  }, [identifier]);

  useEffect(() => {
    if (!allData?.length) return;
    const nextRunDate = shouldAutoSelectLatestRunDate({
      allData,
      previousRunCount: prevAllDataLengthRef.current,
    });
    if (nextRunDate != null) {
      dispatch(applicationSlice.actions.onSetRunDate(nextRunDate));
    }
    prevAllDataLengthRef.current = allData.length;
  }, [allData, dispatch]);

  useEffect(() => {
    if (detectProgramStopTransition(prevIsProgramRunningRef.current, isProgramRunning)) {
      skipNextHistoricalLoadRef.current = true;
    }
    prevIsProgramRunningRef.current = isProgramRunning;
  }, [isProgramRunning]);

  useEffect(() => {
    if (isLiveMode) return;

    const deferHistoricLoad = shouldDeferHistoricLoad({
      skipNextHistoricalLoad: skipNextHistoricalLoadRef.current,
      allData,
    });
    if (deferHistoricLoad) return;

    if (skipNextHistoricalLoadRef.current) {
      skipNextHistoricalLoadRef.current = false;
    }

    const dataToShow = resolveHistoricRun(allData ?? [], runDate);
    if (dataToShow) {
      if (runDate !== dataToShow.runId) {
        dispatch(applicationSlice.actions.onSetRunDate(dataToShow.runId));
      }
      dispatch(applicationSlice.actions.onSaveGraphs(getGraphs(dataToShow.sensorData)));
      return;
    }

    if (shouldClearHistoricGraphsToPlaceholder({ allData, deferHistoricLoad: false })) {
      dispatch(applicationSlice.actions.onSaveGraphs(getGraphs(EMPTY_CHART_PLACEHOLDER as unknown as SensorData[])));
    }
  }, [runDate, allData, dispatch, getGraphs, isLiveMode]);

  // Deterministic stop behavior: when run is stopped and latest run exists,
  // always hydrate graphs with full run timeline (t=0..t=last) once per run.
  useEffect(() => {
    if (programState !== STOP && programState !== STOPPED) return;
    const latest = last(allData ?? []);
    if (!latest) return;
    if (lastHydratedStoppedRunIdRef.current === latest.runId) return;

    lastHydratedStoppedRunIdRef.current = latest.runId;
    sensorObjectsRef.current = {};
    electronicsPortStreamGraphsRef.current = {};
    if (runDate !== latest.runId) {
      dispatch(applicationSlice.actions.onSetRunDate(latest.runId));
    }
    dispatch(applicationSlice.actions.onSaveGraphs(getGraphs(latest.sensorData)));
  }, [programState, STOP, STOPPED, allData, runDate, dispatch, getGraphs]);

  const prevDashboardViewModeForGraphsRef = useRef(dashboardViewMode);

  useEffect(() => {
    const previousMode = prevDashboardViewModeForGraphsRef.current;
    prevDashboardViewModeForGraphsRef.current = dashboardViewMode;
    if (previousMode !== HISTORIC || dashboardViewMode !== LIVE || isProgramRunning) return;
    sensorObjectsRef.current = {};
    electronicsPortStreamGraphsRef.current = {};
    dispatch(applicationSlice.actions.onSaveGraphs(getGraphs(EMPTY_CHART_PLACEHOLDER as unknown as SensorData[])));
  }, [dashboardViewMode, dispatch, getGraphs, isProgramRunning]);

  return (
    <Box flexGrow={1} height="100%" ref={setNodeRef} sx={{ bgcolor: 'background.editor', minHeight: 0 }}>
      <GridStackRenderProvider width={canvasWidth} showGridLines={showGridLines}>
        <GridStackRender />
      </GridStackRenderProvider>
    </Box>
  );
});

export default Dashboard;
